export default function HTMLVEditPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Privacy Policy - HTML Viewer &amp; Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date: <time dateTime="2026-08-04">August 4, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built the{" "}
            <strong>HTML Viewer &amp; Editor</strong> app as an ad-supported
            utility. This page explains what data may be collected and how it
            is used when you use the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Information We Collect
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>No account/login data:</strong> This app does not require
              sign-in, and we do not collect account credentials.
            </li>
            <li>
              <strong>App usage and diagnostics:</strong> Basic analytics,
              device details, and crash/performance data to improve reliability.
            </li>
            <li>
              <strong>Advertising data:</strong> Ad identifiers and ad
              interaction data may be collected by ad partners to serve and
              measure ads.
            </li>
            <li>
              <strong>Text you send to COSMO AI:</strong> when you choose an AI
              action, the text you submit for that action leaves your device and
              is sent to our servers and to Google&apos;s Gemini API so the
              result can be generated. This happens only when you start an AI
              action — the app does not send your files at any other time.
            </li>
            <li>
              <strong>A device identifier:</strong> COSMO AI credits work
              without an account, which means we need a way to recognise your
              device so your credit balance belongs to you. On Android this is
              the Android <strong>App Set ID</strong>; on iOS it is an
              identifier the app generates and stores in the system keychain. We
              store it only as a one-way hash, never in a form that can be
              turned back into your device&apos;s identifier, and it is never
              combined with your advertising ID.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            How We Use Information
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To operate and improve app features and stability.</li>
            <li>To understand app usage trends through analytics.</li>
            <li>To display and measure ads in the app.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            COSMO AI and Credits
          </h2>
          <p>
            HTML Viewer &amp; Editor includes optional AI features powered by{" "}
            <strong>COSMO</strong>, a service Cosmovex operates across its apps.
            COSMO uses a credit balance, and that balance is shared by every
            Cosmovex app with COSMO AI that you use on the same device.{" "}
            <strong>You do not need an account to use it.</strong>
          </p>
          <p>
            Editing, preview and export work without a connection. COSMO AI is
            the one feature that does not: if you start an AI action, the text
            you submit for it is sent off your device so it can be processed.
            Nothing is sent unless you start an AI action.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>What is sent:</strong> only the text you submit for the AI
              action you chose, plus the name of the action. For actions that
              work on part of a page, only the relevant part is sent, not the
              whole file. For the Style action we send the page&apos;s structure
              — its tags, ids and class names — with all text content removed.
            </li>
            <li>
              <strong>Who processes it:</strong> our servers pass the text to
              Google&apos;s Gemini API, which generates the result and returns
              it. Google processes it under its own terms for that service.
            </li>
            <li>
              <strong>What we keep:</strong> the generated result is stored on
              our servers against your anonymous COSMO identity, so that
              repeating or retrying a request does not charge you twice. We also
              keep a record of how many credits each request cost.
            </li>
            <li>
              <strong>How your balance is recognised:</strong> using the hashed
              device identifier described above. There is no name, email, or
              account involved unless you choose to add one.
            </li>
            <li>
              <strong>Optional backup:</strong> you may link a Google account so
              your credits survive moving to a new device. This is entirely
              optional. Every feature — including buying and spending credits —
              works the same whether or not you link one. If you link an
              account, we store the Google account identifier so we can find
              your balance again.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Deleting Your COSMO Account
          </h2>
          <p>
            You can delete your COSMO account from inside any Cosmovex app with
            COSMO AI, or via our{" "}
            <a
              href="https://cosmovexapps.com/account-deletion"
              className="text-[#0F6BBF] underline"
            >
              account deletion page
            </a>
            . Deletion applies to your COSMO account across all of those apps,
            and it removes your credit balance, including any credits you paid
            for. It does not affect any app&apos;s Pro purchase, which stays
            with your Google or Apple account.
          </p>
          <p>
            Deletion is not immediate: for 7 days you can reopen any Cosmovex
            app with COSMO AI and choose Restore. After that we permanently
            delete your linked account identifier, your generated AI results,
            your usage history, and the hashed device identifiers associated
            with the account.
          </p>
          <p>
            <strong>One record is deliberately kept.</strong> When an account is
            deleted we retain a single anonymous entry recording that the
            one-time free credit grant has already been claimed on that device.
            We keep it for fraud prevention: without it, deleting and recreating
            an account would be an unlimited source of free credits. It contains
            only a one-way hash and a flag. It holds no name, email, file
            content, or anything that identifies you, and it is never used for
            any other purpose.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Third-Party Services
          </h2>
          <p>
            HTML Viewer &amp; Editor may use trusted third-party services such
            as:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Google Play Services</li>
            <li>Firebase Analytics and Crashlytics</li>
            <li>Google AdMob</li>
            <li>
              Firebase Authentication, Cloud Firestore, Cloud Functions and App
              Check (COSMO AI)
            </li>
            <li>Google Gemini API (COSMO AI text generation)</li>
            <li>Google Play Billing (COSMO credit purchases)</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Data Retention</h2>
          <p>
            This app has no sign-in, so we do not maintain account profiles.
            Analytics and ad-related data are retained by their respective
            service providers according to their policies.
          </p>
          <p>
            COSMO AI data is retained as described above: generated results and
            credit history stay with your anonymous COSMO identity until you
            delete your COSMO account, after which they are removed apart from
            the single anonymous free-grant record.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Your Choices</h2>
          <p>
            You can limit ad personalization through your device settings and
            stop data collection by uninstalling the app.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Security</h2>
          <p>
            We use reasonable safeguards and rely on trusted providers, but no
            system can be guaranteed 100% secure.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Children&apos;s Privacy
          </h2>
          <p>
            This app is not directed to children under 13, and we do not
            knowingly collect personal information from children.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Changes to This Policy
          </h2>
          <p>
            We may update this policy from time to time. Changes will be posted
            on this page with an updated effective date.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Us</h2>
          <p>
            For privacy questions, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
